import { Title } from '@solidjs/meta'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import MaskInput from '~/components/MaskInput'

export default function MaskInputPage() {
  const [usdValue, setUsdValue] = createSignal('')
  const [copValue, setCopValue] = createSignal('')
  const [ccValue, setCcValue] = createSignal('')

  return (
    <>
      <Title>MaskInput - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">MaskInput</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Entrada de datos con formateo dinámico. MaskInput aplica reglas de máscara en tiempo real para asegurar la integridad de datos monetarios, bancarios y patrones personalizados sin
            sacrificar la UX.
          </p>
        </div>

        <div class="space-y-12">
          {/* Motores de Moneda */}
          <Card>
            <Card.Header
              title="Inteligencia Monetaria"
              subtitle="Formateo automático basado en locale e ISO 4217 con gestión de decimales y símbolos."
              icon={<Icon icon="material-symbols-light:payments-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-10 bg-surface-muted rounded-2xl border border-border">
                <div class="space-y-4">
                  <div class="flex justify-between items-center">
                    <Badge color="info">USD - US Locale</Badge>
                    <span class="text-[10px] font-mono text-foreground-subtle">Raw: {usdValue() || '0'}</span>
                  </div>
                  <MaskInput
                    maskType="currency"
                    currency="USD"
                    locale="en-US"
                    placeholder="$ 0.00"
                    label="Presupuesto Anual"
                    value={usdValue()}
                    onValueChange={setUsdValue}
                    startIcon="material-symbols-light:attach-money"
                  />
                </div>
                <div class="space-y-4">
                  <div class="flex justify-between items-center">
                    <Badge color="success">COP - CO Locale</Badge>
                    <span class="text-[10px] font-mono text-foreground-subtle">Raw: {copValue() || '0'}</span>
                  </div>
                  <MaskInput
                    maskType="currency"
                    currency="COP"
                    locale="es-CO"
                    placeholder="$ 0.00"
                    label="Salario Base"
                    value={copValue()}
                    onValueChange={setCopValue}
                    startIcon="material-symbols-light:payments-outline"
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Patrones de Seguridad y Bancarios */}
          <Card>
            <Card.Header
              title="Patrones de Seguridad"
              subtitle="Máscaras predefinidas para tarjetas de crédito y documentos de identificación."
              icon={<Icon icon="material-symbols-light:security" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-indigo-500">PCI Data Entry</h4>
                  <MaskInput
                    maskType="creditCard"
                    placeholder="0000 0000 0000 0000"
                    label="Número de Tarjeta"
                    value={ccValue()}
                    onValueChange={setCcValue}
                    startIcon="material-symbols-light:credit-card-outline"
                  />
                  <div class="p-4 bg-slate-900 rounded-xl font-mono text-[10px] text-emerald-400 shadow-inner">
                    <span class="opacity-40">// Storage Ready:</span> {ccValue().replace(/\s/g, '')}
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-amber-500">Máscaras Custom</h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <MaskInput maskType="custom" customMask="(999) 999-9999" label="Teléfono" placeholder="(000) 000-0000" />
                    <MaskInput maskType="custom" customMask="AAAA-999-***" label="Código Serial" placeholder="ABCD-123-***" />
                  </div>
                  <div class="flex gap-2">
                    <Badge size="sm" variant="soft" color="primary">
                      9 = Digit
                    </Badge>
                    <Badge size="sm" variant="soft" color="secondary">
                      A = Letter
                    </Badge>
                    <Badge size="sm" variant="soft" color="warning">
                      * = Alphanum
                    </Badge>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="Cinco densidades alineadas con Select y Button: xs, sm, md, lg y xl."
              icon={<Icon icon="material-symbols-light:straighten" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-10 bg-surface-muted rounded-2xl border border-border">
                <MaskInput maskType="currency" size="xs" label="Extra Small" locale="es-CO" currency="COP" />
                <MaskInput maskType="currency" size="sm" label="Small" locale="es-CO" currency="COP" />
                <MaskInput maskType="currency" size="md" label="Medium" locale="es-CO" currency="COP" />
                <MaskInput maskType="currency" size="lg" label="Large" locale="es-CO" currency="COP" />
                <MaskInput maskType="currency" size="xl" label="Extra Large" locale="es-CO" currency="COP" />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente MaskInput."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">maskType</td>
                      <td class="px-6 py-4 italic text-xs">currency | percentage | creditCard | custom</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Algoritmo de máscara a aplicar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic text-xs">'xs' | 'sm' | 'md' | 'lg' | 'xl'</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Altura y tipografía del campo (36/40/48/56/64px).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onValueChange</td>
                      <td class="px-6 py-4 italic text-xs">(raw: string) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Retorna el valor sin formato (ideal para DB).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">locale / currency</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">'en-US' / 'USD'</td>
                      <td class="px-6 py-4">Configuración regional para tipos monetarios.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">customMask</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic text-balance">Patrón para maskType='custom'.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}
